import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  LuArrowRight,
  LuFileText,
  LuMessageCircle,
  LuShieldCheck,
  LuTag,
  LuTruck,
} from "react-icons/lu";
import { RootState } from "../Redux/store";
import { chawlaispat } from "../Redux/apiRoutes";
import usePageMeta from "../common/hooks/usePageMeta";
import { WelcomeSignUp } from "../utils/CustomModal";
import { SignupWait } from "../Redux/reducer/LoginReducer";
import { clearResults } from "../Redux/reducer/Filter";
import { Category } from "./Components/types";
import { titleCase } from "./Components/Navbar";
import { CiButton, CiTag, SectionTitle } from "../common/ci/Ui";
import { whatsappLink } from "../config/contact";
import { useCiT } from "../common/ci/i18n";
import { categoryPath } from "../utils/categoryPath";
import PopularProducts from "./Components/PopularProducts";
import RecentOrdersStrip from "./Components/RecentOrdersStrip";
import defaultImg from "../assets/assets/dummyProduct.png";

interface CompanyRow {
  id: string;
  name: string;
  productCount?: number;
  image?: string;
}

/**
 * Home — design canvas "1 · Home" + "7 · Mobile home".
 * Ek hi component desktop aur mobile dono ke liye (Tailwind breakpoints).
 * Sab data store/API se: categories (Navbar bharta hai), companies, top products,
 * logged-in dealer ke pichle order.
 */
const Home = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useCiT();
  const [welcome, setIsWelcome] = useState<boolean>(false);
  const signupCheck = useSelector((state: RootState) => state.Login.signupWait);
  const allCategories = useSelector(
    (state: RootState) => state.values.allCategories as Category[]
  );
  const allCompanies = useSelector(
    (state: RootState) => state.values.allCompanies as CompanyRow[]
  );
  const userDetails = useSelector(
    (state: RootState) => state.Login.ContractorData
  );

  useEffect(() => {
    setIsWelcome(signupCheck);
  }, [signupCheck]);

  const handleClose = () => {
    setIsWelcome(false);
    dispatch(SignupWait(false));
  };

  usePageMeta(
    "Chawla Ispat Rudrapur — TMT Bar, Sheet, Cement Rate Today",
    "TMT bar, GC sheet, pipe, cement aur building material — rate GST sahit, nearest godown se delivery, GST bill. Chawla Ispat, Rudrapur, Uttarakhand.",
    chawlaispat
  );

  const categories = useMemo(() => {
    const list = Array.isArray(allCategories) ? [...allCategories] : [];
    return list
      .filter((c) => Number(c?.itemCount || 0) > 0)
      .sort((a, b) => Number(b.itemCount || 0) - Number(a.itemCount || 0));
  }, [allCategories]);

  const tmt = useMemo(
    () => categories.find((c) => String(c.name).trim().toUpperCase() === "TMT BAR"),
    [categories]
  );

  const brands = useMemo(() => {
    const list = Array.isArray(allCompanies) ? [...allCompanies] : [];
    return list
      .filter((c) => c && c.name)
      .sort((a, b) => Number(b.productCount || 0) - Number(a.productCount || 0))
      .slice(0, 6);
  }, [allCompanies]);

  const openCategory = (c: Category) => {
    dispatch(clearResults());
    navigate(categoryPath(c), { state: { id: c.id } });
  };

  const totalItems = categories.reduce((s, c) => s + Number(c.itemCount || 0), 0);

  return (
    <div className="bg-ci-bg">
      {/* ---------- Hero ---------- */}
      <section className="mx-auto grid max-w-[1280px] grid-cols-1 gap-6 px-4 pt-6 sm:px-5 sm:pt-9 lg:grid-cols-12 lg:gap-6">
        <div className="flex flex-col justify-center gap-4 lg:col-span-7 lg:gap-5 lg:pr-6">
          <CiTag tone="navy" className="w-max">
            {totalItems ? `${totalItems.toLocaleString("en-IN")}+` : "2,000+"} {t("items")} ·{" "}
            {categories.length || 61} {t("categories")} · {t("Uttarakhand delivery")}
          </CiTag>
          <h1 className="font-display text-[34px] font-bold uppercase leading-[0.98] text-ci-navy sm:text-[48px] lg:text-[64px]">
            {t("Steel, cement & building material —")}{" "}
            <span className="text-ci-orange">{t("seedha godown se")}</span>{t(", GST bill ke saath.")}
          </h1>
          <p className="max-w-[560px] text-[15px] text-ci-muted sm:text-[18px]">
            {t("Rate kg/pcs me dekho, quantity daalo, order confirm karo. Payment cash ya approved credit — delivery nearest godown se.")}
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <CiButton
              variant="orange"
              size="lg"
              onClick={() => (tmt ? openCategory(tmt) : navigate("/items"))}
            >
              {t("Browse TMT Bar")} <LuArrowRight size={18} />
            </CiButton>
            <a
              href={whatsappLink("Namaste, mujhe bulk order ka rate chahiye.")}
              target="_blank"
              rel="noreferrer"
            >
              <CiButton variant="line" size="lg" className="w-full">
                <LuMessageCircle size={18} /> {t("Bulk quote on WhatsApp")}
              </CiButton>
            </a>
          </div>
        </div>

        <div className="flex flex-col gap-4 rounded-lg bg-ci-navy p-6 text-white lg:col-span-5 lg:p-7">
          <span className="font-display text-[22px] font-bold uppercase">{t("Order in 3 steps")}</span>
          {[
            ["1", "Search ya category chuno", "Size, brand, thickness — sab filter me."],
            ["2", "Quantity daalo — kg ya pieces", "Rate GST ke saath dikhega, total turant."],
            ["3", "Confirm — cash / credit on delivery", "Online payment nahi; dealer credit approved account par."],
          ].map(([n, title, sub]) => (
            <div key={n} className="flex items-start gap-3.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ci-orange font-display text-[18px] font-bold">
                {n}
              </span>
              <div className="flex flex-col">
                <b className="text-[16px]">{t(title)}</b>
                <span className="text-[14px] text-[#b8c2cf]">{t(sub)}</span>
              </div>
            </div>
          ))}
          <div className="border-t border-[#34435a] pt-3.5 text-[13px] text-[#b8c2cf]">
            {t("Naye customer ka pehla order approval ke baad confirm hota hai (usually same day).")}
          </div>
        </div>
      </section>

      {/* ---------- Reorder (logged in) ---------- */}
      {userDetails ? (
        <section className="mx-auto max-w-[1280px] px-4 pt-8 sm:px-5 sm:pt-10">
          <RecentOrdersStrip />
        </section>
      ) : null}

      {/* ---------- Categories ---------- */}
      <section className="mx-auto max-w-[1280px] px-4 pt-8 sm:px-5 sm:pt-11">
        <SectionTitle right={<Link to="/categories">{t("All categories")}{categories.length ? ` (${categories.length})` : ""}</Link>}>
          {t("Shop by category")}
        </SectionTitle>
        {categories.length ? (
          <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-4 sm:gap-3.5 lg:grid-cols-6">
            {categories.slice(0, 18).map((c) => (
              <Link
                key={c.id}
                to={categoryPath(c)}
                state={{ id: c.id }}
                onClick={() => dispatch(clearResults())}
                className="flex flex-col gap-2 rounded-lg border border-ci-line bg-white p-2 text-left transition hover:border-ci-navy hover:shadow-sm sm:p-3"
              >
                <span className="block aspect-[5/3] w-full overflow-hidden rounded-md bg-[#eceae5]">
                  <img
                    src={c.image || defaultImg}
                    alt={titleCase(c.name)}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                </span>
                <span className="flex flex-col">
                  <b className="text-[12px] leading-tight sm:text-[14px]">{titleCase(c.name)}</b>
                  <span className="text-[11px] text-ci-muted sm:text-[12px]">{c.itemCount} {t("items")}</span>
                </span>
              </Link>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="h-[120px] animate-pulse rounded-lg bg-ci-line" />
            ))}
          </div>
        )}
      </section>

      {/* ---------- Popular products ---------- */}
      <section className="mx-auto max-w-[1280px] px-4 pt-8 sm:px-5 sm:pt-11">
        <PopularProducts />
      </section>

      {/* ---------- Trust ---------- */}
      <section className="mx-auto grid max-w-[1280px] grid-cols-2 gap-2.5 px-4 pt-8 sm:px-5 sm:pt-11 lg:grid-cols-4 lg:gap-4">
        {[
          [<LuTag size={22} />, "Rate transparent", "Har item par rate GST ke saath — jo dikhe wahi bill me. Koi hidden charge nahi."],
          [<LuTruck size={22} />, "Nearest godown se delivery", "Rudrapur ke 5 godowns me se sabse paas wala assign hota hai."],
          [<LuFileText size={22} />, "GST bill, ERP se seedha", "Order LohaWala ERP me Sales Order banta hai — bill wahi se."],
          [<LuShieldCheck size={22} />, "Dealer credit", "Approved dealer/contractor ko credit; naye customer cash on delivery."],
        ].map(([icon, title, sub], i) => (
          <div key={i} className="flex flex-col gap-1.5 rounded-lg border border-ci-line bg-white p-3.5 sm:gap-2.5 sm:p-5">
            <span className="text-ci-orange">{icon}</span>
            <b className="text-[13px] sm:text-[16px]">{t(title as string)}</b>
            <span className="text-[12px] text-ci-muted sm:text-[14px]">{t(sub as string)}</span>
          </div>
        ))}
      </section>

      {/* ---------- Brands ---------- */}
      {brands.length ? (
        <section className="mx-auto max-w-[1280px] px-4 pt-8 sm:px-5 sm:pt-11">
          <div className="flex flex-wrap items-center gap-x-7 gap-y-3 border-y border-ci-line py-4">
            <span className="text-[12px] uppercase tracking-[0.08em] text-ci-muted">{t("Brands we stock")}</span>
            {brands.map((b) => (
              <Link
                key={b.id}
                to={`/items/${b.id}/company/${encodeURIComponent(b.name)}`}
                className="font-display text-[20px] font-bold uppercase text-ci-muted hover:text-ci-navy"
              >
                {b.name}
              </Link>
            ))}
            <Link to="/company" className="font-display text-[20px] font-bold uppercase text-ci-muted hover:text-ci-navy">
              {t("+ more")}
            </Link>
          </div>
        </section>
      ) : null}

      <div className="h-10 sm:h-14" />
      <WelcomeSignUp isOpen={welcome} setIsOpen={handleClose} />
    </div>
  );
};

export default Home;
